import * as React from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import Link from '@mui/joy/Link';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemContent from '@mui/joy/ListItemContent';
import Typography from '@mui/joy/Typography';
import { Anomaly } from './aggregate';
import { botCardHref } from './LoadShareBar';

type Props = {
    anomalies: Anomaly[];
    selectedIds: number[];
    onSelectBot: (botId: number) => void;
};

const kindColor = (kind: Anomaly['kind']): 'danger' | 'warning' | 'neutral' => {
    switch (kind) {
        case 'highFailRate':
            return 'danger';
        case 'volumeDrop':
            return 'warning';
        case 'zeroLoad':
        default:
            return 'neutral';
    }
};

const kindTitle = (kind: Anomaly['kind']): string => {
    switch (kind) {
        case 'highFailRate':
            return 'Ошибки';
        case 'volumeDrop':
            return 'Падение';
        case 'zeroLoad':
        default:
            return 'Простой';
    }
};

export function AnomaliesList(props: Props) {
    const { anomalies, selectedIds, onSelectBot } = props;
    const selectedSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);

    if (anomalies.length === 0) {
        return (
            <Box>
                <Typography level="title-sm" sx={{ mb: 0.5 }}>
                    Аномалии
                </Typography>
                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                    Нет отклонений за выбранный период
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Typography level="title-sm" sx={{ mb: 0.5 }}>
                Аномалии
            </Typography>
            <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 1 }}>
                Кнопка «На график» добавляет бота на тренд
            </Typography>
            <List size="sm" sx={{ '--List-gap': '6px' }}>
                {anomalies.map((item, idx) => {
                    const onChart = item.botId != null && selectedSet.has(item.botId);
                    return (
                    <ListItem
                        key={`${item.kind}-${item.botId ?? 'x'}-${idx}`}
                        endAction={
                            item.botId != null && !onChart ? (
                                <Button
                                    size="sm"
                                    variant="plain"
                                    onClick={() => onSelectBot(item.botId!)}
                                >
                                    На график
                                </Button>
                            ) : null
                        }
                    >
                        <ListItemContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Chip size="sm" variant="soft" color={kindColor(item.kind)}>
                                    {kindTitle(item.kind)}
                                </Chip>
                                {item.botId != null ? (
                                    <Typography level="body-sm">
                                        <Link
                                            href={botCardHref(item.botId)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            level="body-sm"
                                        >
                                            {item.label}
                                        </Link>
                                        {item.message.startsWith(`${item.label}:`)
                                            ? item.message.slice(item.label.length)
                                            : `: ${item.message}`}
                                    </Typography>
                                ) : (
                                    <Typography level="body-sm">{item.message}</Typography>
                                )}
                            </Box>
                        </ListItemContent>
                    </ListItem>
                    );
                })}
            </List>
        </Box>
    );
}
